import Paragraph from '@pelckmans/elementis/components/paragraph';
import Heading from '@pelckmans/elementis/components/heading';
import { OrderedList, UnorderedList } from '@pelckmans/elementis/components/lists';
import { useRoutes } from '../../../hooks/useRoutes';
import { Cell } from '@pelckmans/elementis/components/table';
import PrivacyTableCell from './components/PrivacyTableCell';

export default function PrivacyPolicyFRContent() {
  const { getRoute } = useRoutes();

  return (
    <div className="break-word privacy-page">
      <Paragraph className="paragraph">Dernière mise à jour le 6/07/2026.</Paragraph>
      <div id="privacy-policy" />
      <Heading level={3} className="heading-3">
        À propos de cette déclaration de confidentialité
      </Heading>
      <Paragraph className="paragraph">
        Pelckmans Portail (ci-après dénommé « nous ») est synonyme d&apos;innovation pédagogique et est un leader en
        matière de concepts didactiques.
      </Paragraph>
      <Paragraph className="paragraph">
        Nous accordons une grande importance au respect de la vie privée de nos clients et des visiteurs (ci-après
        dénommés « vous ») de nos sites web. Voilà pourquoi nous vous informons clairement concernant les précautions et
        mesures que nous prenons dans le cadre du traitement de vos données à caractère personnel.
      </Paragraph>
      <Paragraph className="paragraph">
        Nous établissons cette politique de confidentialité, qui vise à traiter vos données à caractère personnel de
        manière légale, correcte et transparente, en pleine conformité avec la loi du 30 juillet 2018 et le règlement
        (UE) 2016/679.
      </Paragraph>
      <Paragraph className="paragraph">Dans la présente déclaration de confidentialité, nous décrivons :</Paragraph>
      <OrderedList type="A" className="ordered-list">
        <li>Qui nous sommes</li>
        <li>Quelles données à caractère personnel nous collectons et traitons, et pourquoi nous le faisons</li>
        <li>Votre consentement au traitement de vos données à caractère personnel</li>
        <li>Vos droits et la manière dont vous pouvez les exercer</li>
        <li>La durée de conservation de vos données à caractère personnel</li>
        <li>Les tiers avec lesquels nous partageons certaines données et la raison pour laquelle nous le faisons</li>
        <li>Les modifications à la présente déclaration de confidentialité</li>
      </OrderedList>
      <Heading level={3} className="heading-3">
        A. Qui sommes-nous ?
      </Heading>
      <Paragraph className="paragraph">
        Pelckmans Portail fait partie de Pelckmans Uitgevers NV et est une entreprise dont le siège est situé
        Brasschaatsteenweg (C) 308 à 2920 Kalmthout (Belgique), et ayant pour :
      </Paragraph>
      <Paragraph className="paragraph">BCE/TVA : 0444.548.723</Paragraph>
      <Paragraph className="paragraph">
        Adresse e-mail : <a href="mailto:helpdesk@pelckmans.be">helpdesk@pelckmans.be</a>
      </Paragraph>
      <Paragraph className="paragraph">Téléphone : +32 3 660 27 20</Paragraph>
      <Paragraph className="paragraph">
        Si votre école vous demande d&apos;utiliser ou vous autorise à utiliser les ressources pédagogiques numériques
        disponibles sur nos sites web, nous traitons uniquement vos données à caractère personnel sur ordre de
        l&apos;école. L&apos;école est alors responsable du traitement de vos données et est donc le point de contact où
        vous pouvez exercer vos droits. Dans tous les autres cas, nous sommes le responsable du traitement des données.
        Nos coordonnées sont indiquées ci-dessus.
      </Paragraph>
      <Paragraph className="paragraph">Les sites web suivants sont visés :</Paragraph>
      <UnorderedList className="unordered-list">
        <li>
          <a href="https://www.pelckmansportaal.be">https://www.pelckmansportaal.be</a> ;
        </li>
        <li>
          <a href="https://www.pelckmansportail.be">https://www.pelckmansportail.be</a> ;
        </li>
        <li>
          <a href="https://basis.pelckmans.be">https://basis.pelckmans.be</a> ;
        </li>
        <li>
          <a href="https://leerkracht-basis.pelckmans.be">https://leerkracht-basis.pelckmans.be</a> ;
        </li>
        <li>
          <a href="https://zogezegd.pelckmans.be">https://zogezegd.pelckmans.be</a>.
        </li>
        <li>
          <a href="https://leerpoort.be">https://leerpoort.be</a>.
        </li>
      </UnorderedList>
      <Heading level={3} className="heading-3">
        B. Données à caractère personnel traitées et finalité de leur traitement
      </Heading>
      <Paragraph className="paragraph">
        Lorsque vous visitez l&apos;un de nos sites web et/ou que vous vous inscrivez, nous stockons et utilisons vos
        données à caractère personnel afin de pouvoir vous offrir nos services et/ou produits. Plus précisément, nous
        collectons et traitons entre autres les données à caractère personnel suivantes :
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>prénom et nom de famille ;</li>
        <li>école et/ou classe ;</li>
        <li>adresse e-mail.</li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Nous utilisons également des outils pour connaitre les comportements de navigation et de clic sur nos sites web,
        notamment par le biais de cookies. Cela vous évite de devoir remplir une deuxième fois un même formulaire en
        ligne et cela vous offre une expérience optimale de notre site Internet. Notre politique en matière de cookies
        peut être consultée <a href={`${getRoute('TERMS_AND_CONDITIONS')}#cookies`}>ici</a>.
      </Paragraph>
      <Paragraph className="paragraph">
        Nous traitons uniquement vos données à caractère personnel dans l’un des cas suivants :
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>
          lorsque vous avez marqué votre accord exprès à leur collecte ou lorsque leur traitement est indispensable ;
        </li>
        <li>pour l’exécution d’un contrat ;</li>
        <li>pour satisfaire à une obligation légale ou réglementaire ;</li>
        <li>
          pour répondre à toute demande fondée d’accès aux données émanant d’une autorité de contrôle ou subsidiante ;
        </li>
        <li>pour défendre nos intérêts légitimes ou ceux d’un tiers ;</li>
        <li>pour l’administration et la comptabilité.</li>
      </UnorderedList>
      <Heading level={3} className="heading-3">
        C. Consentement
      </Heading>
      <Paragraph className="paragraph">
        En accédant à nos sites web et en les utilisant, vous déclarez avoir lu la présente politique de confidentialité
        et donnez votre consentement libre, spécifique, éclairé et sans équivoque au traitement de vos données à
        caractère personnel.
      </Paragraph>
      <Paragraph className="paragraph">
        Le consentement est donné lorsque vous avez coché la case de la politique de confidentialité.
      </Paragraph>
      <Paragraph className="paragraph">
        Tout accord qui nous lie concernant les services et/ou produits proposés sur notre site web est soumis à votre
        acceptation de la présente politique.
      </Paragraph>
      <Paragraph className="paragraph">
        Vous acceptez que, conformément aux conditions et principes énoncés dans la présente politique, nous puissions
        collecter et traiter les données à caractère personnel que vous communiquez sur le site web ou en lien avec les
        services et/ou produits que nous proposons, aux fins spécifiées ci-dessus.
      </Paragraph>
      <Paragraph className="paragraph">
        Concernant la possibilité de retirer votre consentement, veuillez vous reporter à la section D. « Vos droits
        sont garantis ».
      </Paragraph>
      <Heading level={3} className="heading-3">
        D. Vos droits sont garantis
      </Heading>
      <Paragraph className="paragraph">
        Nous veillons à ce que vous gardiez le contrôle de vos données et puissiez exercer vos droits à tout moment.
      </Paragraph>
      <Paragraph className="paragraph">
        Vous disposez des droits suivants en ce qui concerne vos données à caractère personnel :
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>Droit de consultation :</li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Vous avez le droit de prendre connaissance à tout moment et gratuitement de vos données à caractère personnel,
        ainsi que de l’usage que nous en faisons.
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>Droit de rectification, d&apos;effacement et de limitation du traitement :</li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Vous êtes libre de nous fournir ou non vos données à caractère personnel. En outre, vous avez toujours le droit
        de nous prier de rectifier, de compléter ou d’effacer vos données à caractère personnel. Vous pouvez également
        demander à limiter le traitement de vos données à caractère personnel. D’autres lois qui nous obligent à
        conserver certaines données priment toutefois cette règle.
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>Droit d&apos;opposition :</li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Vous disposez également d’un droit d’opposition au traitement de vos données à caractère personnel pour des
        raisons graves et légitimes.
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>Droit de portabilité des données :</li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Vous disposez du droit d’obtenir vos données à caractère personnel qui sont traitées par nos soins sous une
        forme structurée, courante et lisible par machine et/ou de les transférer à d’autres responsables.
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>Droit de retrait du consentement :</li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Dans la mesure où le traitement est fondé sur votre consentement préalable, vous disposez du droit de retirer ce
        consentement. Vous avez le droit de retirer ce consentement à tout moment. Le retrait du consentement
        n&apos;affecte pas la licéité du traitement fondé sur le consentement préalable.
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>Droit de réclamation :</li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Si vous n&apos;êtes pas d&apos;accord avec la manière dont nous traitons votre demande, vous pouvez vous
        adresser à l&apos;autorité de protection des données (Commission de la protection de la vie privée, rue de la
        Presse 35, 1000 Bruxelles, tél. +32 (0)2 274 48 00, fax +32 (0)2 274 48 35, e-mail{' '}
        <a href="mailto:contact@apd-gba.be">contact@apd-gba.be</a>
        ). Cela n’affecte en rien la possibilité de déposer un recours devant un tribunal civil.
      </Paragraph>
      <Paragraph className="paragraph">
        Si vous subissez un préjudice suite au traitement de vos données à caractère personnel, vous pouvez introduire
        une action en dommages et intérêts.
      </Paragraph>
      <Paragraph className="paragraph">
        Si vous souhaitez exercer vos droits ou si vous avez des questions à ce sujet, veuillez nous contacter à
        l’adresse e-mail : <a href="mailto:helpdesk@pelckmans.be">helpdesk@pelckmans.be</a>
      </Paragraph>
      <Paragraph className="paragraph">
        Dans ce cas, nous vous demanderons quelques informations comme votre carte d’identité, afin de nous assurer que
        personne d’autre n’essaie d’exercer vos droits.
      </Paragraph>
      <Paragraph className="paragraph">
        Le traitement de vos données à caractère personnel ne comprend pas de profilage et nous ne vous soumettrons pas
        non plus à des décisions automatisées.
      </Paragraph>
      <Heading level={3} className="heading-3">
        E. Pendant combien de temps conservons-nous vos données à caractère personnel ?
      </Heading>
      <Paragraph className="paragraph">
        Nous conservons uniquement vos données aussi longtemps que nécessaire pour vous fournir nos services et/ou
        produits et, par la suite, pour une période limitée dans le temps.
      </Paragraph>
      <Paragraph className="paragraph">
        Par exemple, nous évaluons avec qui nous avons encore une relation active justifiant la conservation des données
        à caractère personnel. En l’absence de relation active, nous effaçons les données de notre base de données.
      </Paragraph>
      <Heading level={3} className="heading-3">
        F. Quelles parties peuvent recevoir vos données ?
      </Heading>
      <Paragraph className="paragraph">
        Nous veillons à ce que vos données puissent seulement être vues par les personnes qui en ont besoin pour
        accomplir leurs tâches, par exemple pour vous informer de l’exécution et de l’avancement de nos services et/ou
        produits.
      </Paragraph>
      <Paragraph className="paragraph">
        Nous prenons des mesures techniques et organisationnelles internes pour éviter que vos données à caractère
        personnel soient divulguées à des tiers, soient traitées par des personnes non habilitées à le faire ou soient
        modifiées ou supprimées accidentellement. Nos locaux, nos serveurs, le réseau, les transferts et les données
        sont rigoureusement surveillés.
      </Paragraph>
      <Paragraph className="paragraph">
        Toutefois, il ne s&apos;agit pas d&apos;une garantie absolue. Pour plus d&apos;informations ou en cas de
        présomption d&apos;abus, vous pouvez toujours prendre contact avec nous via l&apos;adresse{' '}
        <a href="mailto:helpdesk@pelckmans.be">helpdesk@pelckmans.be</a>
      </Paragraph>
      <Paragraph className="paragraph">Les parties suivantes reçoivent vos données à caractère personnel :</Paragraph>
      <Heading level={4} className="heading-4">
        1. Partenaires :
      </Heading>
      <Paragraph className="paragraph">Dans le cadre d&apos;une coopération spécifique avec :</Paragraph>
      <UnorderedList className="unordered-list">
        <li>
          Smartschool - Smartbit BV, dont le siège est situé Muizendijkstraat 124 à 3960 Bree (Belgique), et dont le
          numéro de TVA est 0479.803.174 ;
        </li>
        <li>
          AssesmentQ - Televic Education HQ, dont le siège est situé Leo Bekaertlaan 1 à 8870 Izegem (Belgique), et dont
          le numéro de TVA est 0405.314.795 ;
        </li>
        <li>
          Eduhint ODV dont le siège est situé Galileïlaan 31 à 6716 BP Ede (Pays-Bas), et dont le numéro KvK est
          801563240B01;
        </li>
        <li>
          Bookwidgets - Kidimedia BV, dont le siège est situé Leuvensesteenweg 142, 3070 Kortenberg (Belgique), et dont
          le numéro de TVA est 0831.289.505;
        </li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Pour nos sites web et pour la fourniture de nos services et/ou produits, nous partageons vos données avec ces
        partenaires.
      </Paragraph>
      <Paragraph className="paragraph">
        AssessmentQ, Eduhint ODV et Bookwidgets utilisent vos données à caractère personnel afin de leur associer les
        données suivantes :
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>scores/points gagnés ;</li>
        <li>points/score obtenu par question ;</li>
        <li>réponse donnée par question ;</li>
        <li>score par séquence d&apos;exercices (questions multiples) ;</li>
        <li>temps écoulé.</li>
      </UnorderedList>
      <Paragraph className="paragraph">
        Nos partenaires peuvent uniquement utiliser ces données aux fins prévues et seulement le temps nécessaire dans
        le cadre de la collaboration.
      </Paragraph>
      <Heading level={4} className="heading-4">
        2. Pouvoirs publics et autorités :
      </Heading>
      <Paragraph className="paragraph">
        Nous transmettons vos données aux pouvoirs publics et aux autorités si nous y sommes légalement tenus.
      </Paragraph>
      <Heading level={3} className="heading-3">
        G. Modifications à la présente déclaration de confidentialité
      </Heading>
      <Paragraph className="paragraph">
        Nous pouvons actualiser la présente déclaration de confidentialité pour nous conformer aux obligations légales
        en l’espèce. La version la plus récente peut toujours être consultée sur ce site (
        <a href={`${getRoute('TERMS_AND_CONDITIONS')}#privacy-policy`}>
          https://www.pelckmansportail.be/&#35;/politique-de-confidentialite
        </a>
        ).
      </Paragraph>
      <Paragraph className="paragraph">
        Nous vous informons de toute modification importante du contenu par le biais de nos sites web ou d&apos;autres
        canaux.
      </Paragraph>
      <Paragraph className="paragraph">
        Si vous avez des questions précises concernant la confidentialité de vos données, vous pouvez envoyer un message
        à <a href="mailto:helpdesk@pelckmans.be">helpdesk@pelckmans.be</a>
      </Paragraph>
      <div id="cookies" />
      <Heading level={2} className="heading-2">
        Politique en matière de cookies
      </Heading>
      <Heading level={3} className="heading-3">
        À propos de cette politique en matière de cookies
      </Heading>
      <Paragraph className="paragraph">
        Nous utilisons des cookies pour distinguer les utilisateurs du ou des site(s) web. Cela nous permet de vous
        offrir une meilleure expérience de navigation et d&apos;améliorer le(s) site(s) web et leur contenu.
      </Paragraph>
      <Paragraph className="paragraph">
        Dans cette politique en matière de cookies, nous décrivons entre autres :{' '}
      </Paragraph>
      <OrderedList type="A" className="ordered-list">
        <li>Les principes généraux</li>
        <li>Quels cookies sont utilisés et à quelle fin</li>
        <li>La période de conservation</li>
        <li>La gestion des cookies</li>
      </OrderedList>
      <Heading level={3} className="heading-3">
        A. Principes généraux
      </Heading>
      <Paragraph className="paragraph">
        Un « cookie » est un fichier texte qui est stocké de manière temporaire ou permanente sur votre ordinateur ou
        votre téléphone portable lorsque vous visitez notre ou nos site(s) web.
      </Paragraph>
      <Paragraph className="paragraph">
        Les cookies sont permanents lorsqu&apos;ils restent dans votre ordinateur même après la fermeture de votre
        navigateur. Les cookies temporaires disparaissent dès que vous fermez votre navigateur.
      </Paragraph>
      <Paragraph className="paragraph">
        Les cookies envoyés par notre ou nos site(s) web eux-mêmes sont des cookies directs (« cookies de première
        partie »).
      </Paragraph>
      <Paragraph className="paragraph">
        Des cookies peuvent également être installés par des tiers avec lesquels nous coopérons, comme AssesmentQ,
        Eduhint, Bookdwidgets, H5P, Geogebra et Youtube (« cookies tiers »). Ces tierces parties possèdent leurs propres
        politiques de confidentialité et de cookies, qui doivent à leur tour être consultées. Les tiers peuvent modifier
        à tout moment leurs politiques de confidentialité, de cookies, etc. Nous n&apos;avons aucun contrôle sur ces
        services externes.
      </Paragraph>
      <Paragraph className="paragraph">
        Certains des cookies que nous utilisons sont nécessaires au bon fonctionnement de notre ou nos site(s) web,
        d&apos;autres sont nécessaires pour améliorer votre expérience.
      </Paragraph>
      <Paragraph className="paragraph">Vous pouvez modifier ou désactiver les cookies.</Paragraph>
      <Paragraph className="paragraph">
        En utilisant notre ou nos site(s) web, vous acceptez explicitement la gestion des cookies telle que décrite dans
        la présente politique en matière de cookies.
      </Paragraph>
      <Heading level={3} className="heading-3">
        B. Quels cookies utilisons-nous et à quelle fin ?
      </Heading>
      <Paragraph className="paragraph">Nous utilisons différents types de cookies :</Paragraph>
      <UnorderedList className="unordered-list">
        <li>
          Cookies techniques : ils sont nécessaires au fonctionnement de notre ou nos site(s) web, permettent la
          communication des données saisies et sont destinés à faciliter votre navigation ;
        </li>
        <li>
          Cookies analytiques : ils permettent de vous reconnaître et sont utilisés pour compter le nombre
          d&apos;utilisateurs de notre ou nos site(s) web sur une période donnée. Comme ils indiquent également le
          comportement de navigation, ils constituent un moyen efficace d&apos;améliorer votre expérience de navigation
          en vous présentant des suggestions et des offres susceptibles de vous intéresser. Ils nous permettent en outre
          d&apos;identifier et de corriger d&apos;éventuels bugs sur notre ou nos site(s) web ;
        </li>
        <li>
          Cookies fonctionnels : ces cookies facilitent l&apos;utilisation de notre ou nos site(s) web en conservant
          certains choix saisis (par exemple, le nom d&apos;utilisateur ou la langue).
        </li>
      </UnorderedList>
      <Paragraph className="paragraph">
        La législation nous permet d’utiliser les cookies essentiels sans votre autorisation. Le retrait de votre accord
        à l’utilisation des cookies n’a pas d’incidence sur ces cookies.
      </Paragraph>
      <Heading level={3} className="heading-3">
        C. Période de conservation des cookies
      </Heading>
      <Paragraph className="paragraph">
        Les cookies sont conservés pendant la durée nécessaire pour atteindre l&apos;objectif visé.
      </Paragraph>
      <Paragraph className="paragraph">
        Voici les cookies susceptibles d&apos;être stockés sur votre disque dur à la suite de votre visite sur notre ou
        nos site(s) web ainsi que la durée de conservation de ces cookies :
      </Paragraph>
      <div className="cookies-table--wrapper">
        {/* eslint-disable-next-line local-rules/table-accessible-name */}
        <table className="table cookies-table">
          <thead>
            <tr>
              <Cell.HeaderText title="Cookie" />
              <Cell.HeaderText title="Description" />
              <Cell.HeaderText title="Durée" />
              <Cell.HeaderText title="Fournisseur" />
            </tr>
          </thead>
          <tbody>
            <tr>
              <PrivacyTableCell title="_ga" />
              <PrivacyTableCell title="enregistre un identifiant unique qui est utilisé pour générer des données statistiques sur la façon dont vous utilisez le(s) site(s) web." />
              <PrivacyTableCell title="2 ans" />
              <PrivacyTableCell title="Google Tag Manager / Google" />
            </tr>
            <tr>
              <PrivacyTableCell title="_gid" />
              <PrivacyTableCell title="enregistre un identifiant unique qui est utilisé pour générer des données statistiques sur la façon dont vous utilisez le(s) site(s) web, et plus particulièrement sur le comportement de groupe." />
              <PrivacyTableCell title="1 jour" />
              <PrivacyTableCell title="Google Tag Manager / Google" />
            </tr>
            <tr>
              <PrivacyTableCell title="_gat" />
              <PrivacyTableCell title="est utilisé par Google Analytics pour ralentir le taux de requête. " />
              <PrivacyTableCell title="1 jour" />
              <PrivacyTableCell title="Google Tag Manager / Google" />
            </tr>
            <tr>
              <PrivacyTableCell title="p-local" />
              <PrivacyTableCell title="est utilisé pour conserver la langue." />
              <PrivacyTableCell title="Fin de la session" />
              <PrivacyTableCell title="Pelckmans Portail" />
            </tr>
            <tr>
              <PrivacyTableCell title="p-authenticated" />
              <PrivacyTableCell title="est utilisé pour conserver votre statut de connexion." />
              <PrivacyTableCell title="Fin de la session" />
              <PrivacyTableCell title="Pelckmans Portail" />
            </tr>
            <tr>
              <PrivacyTableCell title="_cl" />
              <PrivacyTableCell title="conserve votre acceptation ou non des cookies." />
              <PrivacyTableCell title="1 an" />
              <PrivacyTableCell title="Pelckmans Portail" />
            </tr>
          </tbody>
        </table>
      </div>
      <Heading level={3} className="heading-3">
        D. Gestion des cookies
      </Heading>
      <Paragraph className="paragraph">
        Si vous ne souhaitez pas que notre ou nos site(s) web place(nt) des cookies sur votre disque dur, vous pouvez
        facilement les gérer ou les supprimer en ajustant les paramètres de votre navigateur. En programmant votre
        navigateur, vous pouvez également recevoir une notification lorsque notre ou nos site(s) web utilise(nt) des
        cookies et ainsi décider de les accepter ou de les refuser.
      </Paragraph>
      <Paragraph className="paragraph">
        Si vous désactivez certains cookies, vous acceptez que le(s) site(s) web ne fonctionne(nt) pas de manière
        optimale. Certaines parties du ou des site(s) web peuvent ne pas être utilisables ou l&apos;être partiellement.
      </Paragraph>
      <Paragraph className="paragraph">
        Si vous souhaitez gérer et/ou supprimer certains cookies de cette manière, vous pouvez le faire via le(s)
        lien(s) suivant(s) :
      </Paragraph>
      <UnorderedList className="unordered-list">
        <li>
          Internet Explorer :
          <a
            target="_blank"
            rel="noreferrer"
            href="https://support.microsoft.com/fr-fr/windows/supprimer-et-g%C3%A9rer-les-cookies-168dab11-0753-043d-7c16-ede5947fc64d"
          >
            https://support.microsoft.com/fr-fr/windows/supprimer-et-g%C3%A9rer-les-cookies-168dab11-0753-043d-7c16-ede5947fc64d
          </a>{' '}
          ;
        </li>
        <li>
          Microsoft Edge :
          <a
            target="_blank"
            rel="noreferrer"
            href="https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
          >
            https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09
          </a>{' '}
          ;
        </li>
        <li>
          Chrome :
          <a
            target="_blank"
            rel="noreferrer"
            href="https://support.google.com/accounts/answer/32050?hl=fr&co=GENIE.Platform%3DDesktop"
          >
            https://support.google.com/accounts/answer/32050?hl=fr&co=GENIE.Platform%3DDesktop
          </a>{' '}
          ;
        </li>
        <li>
          Firefox :
          <a
            target="_blank"
            rel="noreferrer"
            href="https://support.mozilla.org/fr/kb/effacer-les-cookies-pour-supprimer-les-information"
          >
            https://support.mozilla.org/fr/kb/effacer-les-cookies-pour-supprimer-les-information
          </a>{' '}
          ;
        </li>
        <li>
          Safari :
          <a target="_blank" rel="noreferrer" href="https://support.apple.com/fr-be/guide/safari/sfri11471/mac">
            https://support.apple.com/fr-be/guide/safari/sfri11471/mac
          </a>{' '}
          .
        </li>
      </UnorderedList>
      <Paragraph className="paragraph">Si nécessaire, consultez l’aide de votre navigateur.</Paragraph>
      <Paragraph className="paragraph">
        Vous avez encore des questions ? Contactez-nous à l’adresse{' '}
        <a href="mailto:helpdesk@pelckmans.be">helpdesk@pelckmans.be</a>
      </Paragraph>
    </div>
  );
}
